if (!customElements.get('sticky-product-cta')) {
  customElements.define(
    'sticky-product-cta',
    class StickyProductCta extends HTMLElement {
      constructor() {
        super();

        this.mediaQuery = window.matchMedia('(max-width: 989px)');
        this.placeholders = new Map();
        this.hasPassedSentinel = false;

        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);
        this.handleSubmitIntent = this.handleSubmitIntent.bind(this);
        this.handleProductAdded = this.handleProductAdded.bind(this);
        this.handleProductSubmit = this.handleProductSubmit.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleCartUpdate = this.handleCartUpdate.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        this.trigger = this.querySelector('[data-sticky-cta-trigger]');
        this.triggerLabel = this.querySelector('.pdp-sticky-cta__trigger-label');
        this.overlay = this.querySelector('.pdp-sticky-cta__overlay');
        this.sheet = this.querySelector('.pdp-sticky-cta__sheet');
        this.closeButtons = this.querySelectorAll('[data-sticky-cta-close]');
        this.slots = {
          'variant-picker': this.querySelector('[data-sticky-cta-slot="variant-picker"]'),
          quantity: this.querySelector('[data-sticky-cta-slot="quantity"]'),
          'buy-buttons': this.querySelector('[data-sticky-cta-slot="buy-buttons"]'),
        };

        this.bindEvents();
        this.refresh();
        this.observeMutations();
        this.handleScroll();
      }

      disconnectedCallback() {
        this.closeSheet({ restoreOnly: true });
        this.mutationObserver?.disconnect();
        this.mediaQuery.removeEventListener('change', this.handleMediaQueryChange);
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleScroll);
        this.trigger?.removeEventListener('click', this.handleTriggerClick);
        this.closeButtons?.forEach((button) => button.removeEventListener('click', this.handleCloseClick));
        document.removeEventListener('keydown', this.handleKeydown);
        this.productInfo?.removeEventListener('click', this.handleSubmitIntent, true);
        this.removeEventListener('click', this.handleSubmitIntent, true);
        this.productInfo?.removeEventListener('product-form:added', this.handleProductAdded);
        this.removeEventListener('product-form:added', this.handleProductAdded);
        this.productInfo?.removeEventListener('submit', this.handleProductSubmit, true);
        this.removeEventListener('submit', this.handleProductSubmit, true);
        this.cartUpdateUnsubscriber?.();
      }

      bindEvents() {
        this.trigger?.addEventListener('click', this.handleTriggerClick);
        this.closeButtons?.forEach((button) => button.addEventListener('click', this.handleCloseClick));
        this.mediaQuery.addEventListener('change', this.handleMediaQueryChange);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleScroll);
        document.addEventListener('keydown', this.handleKeydown);
        this.productInfo.addEventListener('click', this.handleSubmitIntent, true);
        this.addEventListener('click', this.handleSubmitIntent, true);
        this.productInfo.addEventListener('product-form:added', this.handleProductAdded);
        this.addEventListener('product-form:added', this.handleProductAdded);
        this.productInfo.addEventListener('submit', this.handleProductSubmit, true);
        this.addEventListener('submit', this.handleProductSubmit, true);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
        }
      }

      handleMediaQueryChange() {
        if (!this.mediaQuery.matches) {
          this.closeSheet({ restoreOnly: true });
          this.classList.remove('is-visible');
        } else {
          this.refresh();
          this.handleScroll();
        }
      }

      handleScroll() {
        if (!this.mediaQuery.matches || !this.sentinel) {
          this.hasPassedSentinel = false;
          this.updateVisibility();
          return;
        }

        const rect = this.sentinel.getBoundingClientRect();
        this.hasPassedSentinel = rect.top <= 0;
        this.updateVisibility();
      }

      observeMutations() {
        this.mutationObserver?.disconnect();
        this.mutationObserver = new MutationObserver(() => this.refresh());
        this.mutationObserver.observe(this.buyButtonsItem || this.productInfo, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class', 'value'],
        });
      }

      refresh() {
        this.buyButtonsItem = this.getItem('buy-buttons');
        this.variantPickerItem = this.getItem('variant-picker');
        this.quantityItem = this.getItem('quantity');
        this.sentinel = this.productInfo.querySelector('[data-sticky-cta-sentinel]');
        this.updateSheetSubmitLabel();
        this.updateTriggerState();
        this.handleScroll();
        this.updateVisibility();
      }

      getItem(role) {
        const selector = `[data-sticky-cta-item="${role}"]`;
        return this.productInfo?.querySelector(selector) || this.querySelector(selector);
      }

      getSubmitButton() {
        return (
          this.querySelector(`#ProductSubmitButton-${this.dataset.section}`) ||
          this.querySelector('.product-form__submit') ||
          this.productInfo?.querySelector(`#ProductSubmitButton-${this.dataset.section}`) ||
          this.productInfo?.querySelector('.product-form__submit')
        );
      }

      getSubmitLabelElement() {
        return this.getItem('buy-buttons')?.querySelector('.product-form__submit .product-form__submit-label') || null;
      }

      getSubmitLabelState() {
        const submitButton = this.getSubmitButton();
        const submitLabel = submitButton?.querySelector('.product-form__submit-label') || null;
        const priceText = this.getPriceText();
        const isDisabled =
          !submitButton ||
          submitButton.hasAttribute('disabled') ||
          submitButton.getAttribute('aria-disabled') === 'true';

        let currentLabel =
          submitLabel?.textContent?.trim() ||
          submitButton?.textContent?.trim() ||
          window.variantStrings?.addToCart ||
          'Add to cart';

        if (priceText) {
          const pricedSuffix = ` - ${priceText}`;
          if (currentLabel.endsWith(pricedSuffix)) {
            currentLabel = currentLabel.slice(0, currentLabel.length - pricedSuffix.length).trim();
          }
        }

        if (submitLabel && !isDisabled && currentLabel) {
          submitLabel.dataset.baseLabel = currentLabel;
        }

        return {
          submitButton,
          submitLabel,
          priceText,
          isDisabled,
          baseLabel:
            (isDisabled ? currentLabel : submitLabel?.dataset.baseLabel || currentLabel) ||
            window.variantStrings?.addToCart ||
            'Add to cart',
        };
      }

      getPriceText() {
        const priceRoot = this.productInfo?.querySelector(`#price-${this.dataset.section}`);
        if (!priceRoot) return '';

        return (
          priceRoot.querySelector('.price__sale .price-item--sale')?.textContent?.trim() ||
          priceRoot.querySelector('.price__regular .price-item--regular:not(.variant-item__old-price)')?.textContent?.trim() ||
          ''
        );
      }

      hasVariantOptions() {
        if (this.dataset.productHasVariants !== 'true') return false;
        return !!this.variantPickerItem?.querySelector('.product-form__input, variant-selects');
      }

      hasAvailableVariants() {
        return this.dataset.productHasAvailableVariants === 'true';
      }

      getCurrentVariantId() {
        return (
          this.querySelector(`#product-form-${this.dataset.section} [name="id"]`)?.value ||
          this.querySelector(`#product-form-installment-${this.dataset.section} [name="id"]`)?.value ||
          this.querySelector('.product-variant-id')?.value ||
          this.productInfo?.querySelector(`#product-form-${this.dataset.section} [name="id"]`)?.value ||
          this.productInfo?.querySelector(`#product-form-installment-${this.dataset.section} [name="id"]`)?.value ||
          this.productInfo?.querySelector('.product-variant-id')?.value ||
          null
        );
      }

      setLastSubmittedVariantIdFromTarget(target) {
        const productForm =
          target?.closest?.('product-form') ||
          target?.closest?.('form')?.closest?.('product-form') ||
          null;

        const form =
          productForm?.querySelector?.('form') ||
          target?.closest?.('form') ||
          null;

        const variantId =
          productForm?.variantIdInput?.value ||
          form?.querySelector?.('[name="id"]')?.value ||
          this.getCurrentVariantId();

        this.lastSubmittedVariantId = variantId || null;
      }

      updateSheetSubmitLabel() {
        const { submitLabel, priceText, isDisabled, baseLabel } = this.getSubmitLabelState();
        if (!submitLabel) return;
        const nextLabel =
          !isDisabled && this.classList.contains('is-sheet-open') && priceText
            ? `${baseLabel} - ${priceText}`
            : baseLabel;

        if (submitLabel.textContent.trim() !== nextLabel) {
          submitLabel.textContent = nextLabel;
        }
      }

      updateTriggerState() {
        if (!this.trigger) return;

        const { submitButton, priceText, isDisabled, baseLabel } = this.getSubmitLabelState();
        const hasVariantOptions = this.hasVariantOptions();
        const hasAvailableVariants = this.hasAvailableVariants();
        const useVariantPickerTrigger = hasVariantOptions && hasAvailableVariants;
        const triggerIsDisabled = !submitButton || (!useVariantPickerTrigger && isDisabled);
        const triggerBaseLabel =
          useVariantPickerTrigger && isDisabled
            ? window.variantStrings?.addToCart || 'Add to cart'
            : baseLabel;
        const triggerLabel = !triggerIsDisabled && priceText ? `${triggerBaseLabel} - ${priceText}` : triggerBaseLabel;

        if (this.triggerLabel.textContent.trim() !== triggerLabel) {
          this.triggerLabel.textContent = triggerLabel;
        }
        this.trigger.disabled = triggerIsDisabled;
        this.classList.toggle('pdp-sticky-cta--has-variants', hasVariantOptions);
      }

      updateVisibility() {
        const shouldShow =
          this.mediaQuery.matches &&
          !!this.buyButtonsItem &&
          this.hasPassedSentinel &&
          !this.classList.contains('is-sheet-open');

        this.classList.toggle('is-visible', shouldShow);
      }

      handleTriggerClick(event) {
        event.preventDefault();
        if (this.trigger?.disabled) return;

        if (this.hasVariantOptions()) {
          this.openSheet();
          return;
        }

        this.getSubmitButton()?.click();
      }

      handleCloseClick(event) {
        event.preventDefault();
        this.closeSheet();
      }

      handleKeydown(event) {
        if (event.key === 'Escape' && this.classList.contains('is-sheet-open')) {
          this.closeSheet();
        }
      }

      handleSubmitIntent(event) {
        if (!this.classList.contains('is-sheet-open')) return;

        const submitButton = event.target.closest('button[type="submit"], input[type="submit"], .product-form__submit');
        if (!submitButton) return;

        this.setLastSubmittedVariantIdFromTarget(submitButton);

        window.setTimeout(() => {
          if (this.classList.contains('is-sheet-open')) {
            this.closeSheet();
          }
        }, 0);
      }

      handleProductSubmit(event) {
        if (!this.classList.contains('is-sheet-open')) return;
        this.setLastSubmittedVariantIdFromTarget(event.target);

        window.setTimeout(() => {
          if (this.classList.contains('is-sheet-open')) {
            this.closeSheet();
          }
        }, 0);
      }

      handleProductAdded(event) {
        if (!this.classList.contains('is-sheet-open')) return;

        const addedVariantId = event?.detail?.productVariantId || null;
        const candidateVariantIds = [this.lastSubmittedVariantId, this.getCurrentVariantId()]
          .filter(Boolean)
          .map((id) => String(id));

        if (addedVariantId && candidateVariantIds.length && !candidateVariantIds.includes(String(addedVariantId))) return;

        this.closeSheet();
        this.lastSubmittedVariantId = null;
      }

      handleCartUpdate(event = {}) {
        if (!this.classList.contains('is-sheet-open')) return;
        if (event?.source && event.source !== 'product-form') return;

        const updatedVariantId = event?.productVariantId || event?.data?.productVariantId || null;
        const candidateVariantIds = [this.lastSubmittedVariantId, this.getCurrentVariantId()]
          .filter(Boolean)
          .map((id) => String(id));

        if (updatedVariantId && candidateVariantIds.length && !candidateVariantIds.includes(String(updatedVariantId))) return;

        this.closeSheet();
        this.lastSubmittedVariantId = null;
      }

      openSheet() {
        if (!this.mediaQuery.matches || this.classList.contains('is-sheet-open')) return;

        this.portalItem('variant-picker');
        this.portalItem('quantity');
        this.portalItem('buy-buttons');

        this.classList.add('is-sheet-open');
        this.sheet?.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('pdp-sticky-cta-lock');
        document.body.classList.add('pdp-sticky-cta-lock');
        this.updateSheetSubmitLabel();

        window.requestAnimationFrame(() => {
          this.querySelector('.pdp-sticky-cta__sheet-close')?.focus();
        });
      }

      closeSheet({ restoreOnly = false } = {}) {
        this.restoreItem('variant-picker');
        this.restoreItem('quantity');
        this.restoreItem('buy-buttons');
        this.lastSubmittedVariantId = null;

        this.classList.remove('is-sheet-open');
        this.sheet?.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('pdp-sticky-cta-lock');
        document.body.classList.remove('pdp-sticky-cta-lock');
        this.updateSheetSubmitLabel();

        if (!restoreOnly) {
          this.refresh();
        }
      }

      portalItem(role) {
        const item = this.getItem(role);
        const target = this.slots[role];
        if (!item || !target || item.parentElement === target) return;

        const placeholder = document.createElement('div');
        placeholder.hidden = true;
        placeholder.dataset.stickyCtaPlaceholder = role;

        item.before(placeholder);
        this.placeholders.set(role, placeholder);
        item.classList.add('is-in-sticky-sheet');
        target.appendChild(item);
      }

      restoreItem(role) {
        const item = this.getItem(role);
        const placeholder = this.placeholders.get(role);

        if (!item || !placeholder || !placeholder.parentNode) return;

        placeholder.parentNode.insertBefore(item, placeholder);
        item.classList.remove('is-in-sticky-sheet');
        placeholder.remove();
        this.placeholders.delete(role);
      }
    }
  );
}
